import * as React from 'react'
import { ReactElement, useMemo, useState } from 'react'
import { useFormikContext } from 'formik'
import { FamilyLawModuleDto, OffspringDto } from '../../../../../hooks/module/useFamilyLaw'
import { useThemeClassName_unstable } from '@fluentui/react-shared-contexts'
import { RelationshipType } from '../../Relationship'
import { Field, Input } from '@fluentui/react-components'
import { Acknowledgement, AcknowledgementMap, CustodyMap, CustodyOptions, MainstayMap, MainstayOptions } from './OffspringDialog'
import LocalDatePicker from '../../../../../components/DatePicker/LocalDatePicker'
import { Gender } from '../../../../../config/contactConfig'
import { css } from '@fluentui/react'
import BaseCombobox from '../../../../Combobox/BaseCombobox'

type Props = {
    offspring: OffspringDto
    setOffspring: (offspring: OffspringDto) => void
    horizontal: boolean
    styles: Record<'minWidth' | 'formRow' | 'side' | 'fullName' | 'firstNames' | 'details', string>
}

export const genderOptions = [
    { key: Gender.Male, text: 'Man' },
    { key: Gender.Female, text: 'Vrouw' },
    { key: Gender.Other, text: 'Overig / Non-binair' },
]

export function Body({ offspring, setOffspring, horizontal, styles }: Props): ReactElement {
    const { values } = useFormikContext<FamilyLawModuleDto>()
    const themeClassName = useThemeClassName_unstable()
    const [, setMainstay] = useState<string>(MainstayMap.get(offspring.mainstay))
    const [, setCustody] = useState<string>(CustodyMap.get(offspring.custody))
    const [, setAcknowledgement] = useState<string>(AcknowledgementMap.get(offspring.acknowledgement))
    const AcknowledgementOptions = useMemo(
        () =>
            [
                {
                    key: Acknowledgement.Birth,
                    text: AcknowledgementMap.get(Acknowledgement.Birth),
                    filter: () => true,
                },
                {
                    key: Acknowledgement.Later,
                    text: AcknowledgementMap.get(Acknowledgement.Later),
                    filter: () => true,
                },
                {
                    key: Acknowledgement.No,
                    text: AcknowledgementMap.get(Acknowledgement.No),
                    filter: () => true,
                },
                {
                    key: Acknowledgement.ByLaw,
                    text: AcknowledgementMap.get(Acknowledgement.ByLaw),
                    filter: () => values.relationshipType !== RelationshipType.Cohabitation,
                },
                {
                    key: Acknowledgement.Misc,
                    text: AcknowledgementMap.get(Acknowledgement.Misc),
                    filter: () => true,
                },
            ].filter(option => option.filter()),
        [values.relationshipType],
    )
    return (
        <div className={styles.formRow}>
            <div className={css(styles.side, horizontal ? styles.fullName : undefined)}>
                <Field label="Roepnaam" required>
                    <Input
                        className={styles.minWidth}
                        value={offspring.child.name.given}
                        onChange={(_, data) =>
                            setOffspring({
                                ...offspring,
                                child: { ...offspring.child, name: { ...offspring.child.name, given: data.value } },
                            })
                        }
                    />
                </Field>
                <Field label="Tussenvoegsels">
                    <Input
                        className={styles.minWidth}
                        value={offspring.child.name.insertion}
                        onChange={(_, data) =>
                            setOffspring({
                                ...offspring,
                                child: { ...offspring.child, name: { ...offspring.child.name, insertion: data.value } },
                            })
                        }
                    />
                </Field>
                <Field label="Achternaam" required>
                    <Input
                        className={styles.minWidth}
                        value={offspring.child.name.last}
                        onChange={(_, data) =>
                            setOffspring({
                                ...offspring,
                                child: { ...offspring.child, name: { ...offspring.child.name, last: data.value } },
                            })
                        }
                    />
                </Field>
            </div>
            <div className={css(styles.side, horizontal ? styles.firstNames : undefined)}>
                <Field label="Voorletters" required>
                    <Input
                        className={styles.minWidth}
                        value={offspring.child.name.initials}
                        onChange={(_, data) =>
                            setOffspring({
                                ...offspring,
                                child: { ...offspring.child, name: { ...offspring.child.name, initials: data.value } },
                            })
                        }
                    />
                </Field>
                <Field label="Voornamen" required>
                    <Input
                        value={offspring.child.name.first}
                        onChange={(_, data) =>
                            setOffspring({
                                ...offspring,
                                child: { ...offspring.child, name: { ...offspring.child.name, first: data.value } },
                            })
                        }
                    />
                </Field>
            </div>
            <div className={css(styles.side, horizontal ? styles.details : undefined)}>
                <Field label="Geslacht" required>
                    <BaseCombobox
                        onOptionSelect={(_, data) => {
                            setOffspring({
                                ...offspring,
                                child: {
                                    ...offspring.child,
                                    gender: Object.values(Gender).find(option => option.toString() === data.optionValue),
                                },
                            })
                        }}
                        selectedOptions={[offspring.child.gender]}
                        entities={genderOptions}
                        getEntityLabel={entity => entity.text}
                        getEntityValue={entity => entity.key}
                    />
                </Field>
                <Field label="Geboortedatum" required>
                    <LocalDatePicker
                        popupSurface={{ className: themeClassName }}
                        isoValue={offspring.child.dateOfBirth}
                        onSelectDate={date => {
                            setOffspring({ ...offspring, child: { ...offspring.child, dateOfBirth: date } })
                        }}
                    />
                </Field>
            </div>
            <Field label="BSN / Persoonsnummer" required>
                <Input
                    value={offspring.child.identificationNumber}
                    onChange={(_, data) => {
                        setOffspring({ ...offspring, child: { ...offspring.child, identificationNumber: data.value } })
                    }}
                />
            </Field>
            <Field label="Hoofdverblijfplaats" required>
                <BaseCombobox
                    onOptionSelect={(_, data) => {
                        setOffspring({ ...offspring, mainstay: data.optionValue })
                        setMainstay(data.optionText)
                    }}
                    entities={MainstayOptions}
                    getEntityLabel={entity => entity.text}
                    getEntityValue={entity => entity.key}
                    selectedOptions={[offspring.mainstay]}
                />
            </Field>
            <Field label="Gezag" required>
                <BaseCombobox
                    onOptionSelect={(_, data) => {
                        setOffspring({ ...offspring, custody: data.optionValue })
                        setCustody(data.optionText)
                    }}
                    entities={CustodyOptions}
                    getEntityLabel={entity => entity.text}
                    getEntityValue={entity => entity.key}
                    selectedOptions={[offspring.custody]}
                />
            </Field>
            <Field label="Erkenning door vader" required>
                <BaseCombobox
                    onOptionSelect={(_, data) => {
                        setOffspring({ ...offspring, acknowledgement: data.optionValue })
                        setAcknowledgement(data.optionText)
                    }}
                    entities={AcknowledgementOptions}
                    getEntityLabel={entity => entity.text}
                    getEntityValue={entity => entity.key}
                    selectedOptions={[offspring.acknowledgement]}
                />
            </Field>
        </div>
    )
}
